import { useState, useEffect, useRef, useCallback } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/enhanced-button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  FileText,
  Receipt,
  CreditCard,
  DollarSign,
  Landmark,
  FileCheck,
  FileMinus,
  Truck,
  Users,
  RefreshCw,
  ChevronDown,
  Filter,
  Check,
  X,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActivityLog } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 100;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type DirectionFilter = "all" | "money_in" | "money_out" | "documents";

/** Normalized category for display */
type FeedCategory =
  | "Invoice"
  | "Customer Payment"
  | "Vendor Payment"
  | "Bill"
  | "Expense"
  | "Credit Memo"
  | "Estimate"
  | "Purchase Order"
  | "Journal Entry"
  | "Transfer"
  | "Deposit"
  | "Customer"
  | "Vendor"
  | "Sync"
  | "Other";

type MoneyDirection = "in" | "out" | "neutral";

interface FeedItem {
  id: string;
  category: FeedCategory;
  narrative: React.ReactNode;
  relativeTime: string;
  direction: MoneyDirection;
  raw: ActivityLog;
}


// ---------------------------------------------------------------------------
// Category resolution
// ---------------------------------------------------------------------------

const CATEGORY_MAP: Record<string, FeedCategory> = {
  invoice: "Invoice",
  invoice_created: "Invoice",
  invoice_updated: "Invoice",
  invoice_deleted: "Invoice",
  customer_payment: "Customer Payment",
  payment_received: "Customer Payment",
  payment: "Customer Payment",
  vendor_payment: "Vendor Payment",
  payment_made: "Vendor Payment",
  bill: "Bill",
  bill_created: "Bill",
  bill_updated: "Bill",
  bill_deleted: "Bill",
  expense: "Expense",
  expense_created: "Expense",
  expense_updated: "Expense",
  purchase: "Expense",
  credit_memo: "Credit Memo",
  credit_memo_created: "Credit Memo",
  estimate: "Estimate",
  estimate_created: "Estimate",
  purchase_order: "Purchase Order",
  journal_entry: "Journal Entry",
  transfer: "Transfer",
  deposit: "Deposit",
  customer: "Customer",
  customer_created: "Customer",
  customer_updated: "Customer",
  vendor: "Vendor",
  vendor_created: "Vendor",
  vendor_updated: "Vendor",
  sync: "Sync",
  sync_completed: "Sync",
  sync_started: "Sync",
};

const resolveCategory = (log: ActivityLog): FeedCategory => {
  if (log.category) {
    const mapped =
      CATEGORY_MAP[log.category.toLowerCase().replace(/\s+/g, "_")];
    if (mapped) return mapped;
  }
  const key = log.activity_type.toLowerCase().replace(/\s+/g, "_");
  return CATEGORY_MAP[key] || "Other";
};

// ---------------------------------------------------------------------------
// Money direction
// ---------------------------------------------------------------------------

const DIRECTION_MAP: Record<FeedCategory, MoneyDirection> = {
  Invoice: "in",
  "Customer Payment": "in",
  Deposit: "in",
  "Vendor Payment": "out",
  Bill: "out",
  Expense: "out",
  "Purchase Order": "out",
  "Credit Memo": "neutral",
  Estimate: "neutral",
  "Journal Entry": "neutral",
  Transfer: "neutral",
  Customer: "neutral",
  Vendor: "neutral",
  Sync: "neutral",
  Other: "neutral",
};

const resolveDirection = (cat: FeedCategory): MoneyDirection =>
  DIRECTION_MAP[cat] ?? "neutral";

// ---------------------------------------------------------------------------
// Narrative builder
// ---------------------------------------------------------------------------

const formatCurrency = (amount: number, currency?: string) => {
  const cur = currency || "USD";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: cur,
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  }
};

const formatDueDate = (dateStr: string) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const cleanupDetail = (detail: string): string => {
  // Remove database ID references like "(ID: 16)" or "ID 16"
  return detail
    .replace(/\(ID:\s*\d+\)/gi, "")
    .replace(/\bID\s*\d+\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
};

// Bold helper for JSX narratives
const B = ({ children }: { children: React.ReactNode }) => (
  <strong className="font-semibold">{children}</strong>
);

const buildNarrative = (log: ActivityLog, category: FeedCategory): React.ReactNode => {
  const activityType = log.activity_type.toLowerCase();
  const isCreated = activityType.includes('created');
  const isUpdated = activityType.includes('updated');
  const isDeleted = activityType.includes('deleted');

  const amt = log.amount != null ? formatCurrency(log.amount, log.currency) : null;
  const party = log.counterparty || log.entity_display_name;
  const name = log.entity_display_name;
  const status = log.status;
  const due = log.due_date ? formatDueDate(log.due_date) : null;
  const memo = log.memo;
  const currency = log.currency || "USD";

  const action = isCreated ? "created" : isUpdated ? "updated" : isDeleted ? "deleted" : null;

  switch (category) {
    case "Invoice": {
      const meta: string[] = [];
      if (status) meta.push(`Status: ${status}`);
      if (due) meta.push(`Due: ${due}`);

      return (
        <span>
          Invoice {name && <B>{name}</B>}
          {amt && <>{" "}for <B>{amt} {currency}</B></>}
          {action && ` ${action}`}
          {log.counterparty && <>{" "}for <B>{log.counterparty}</B></>}
          {meta.length > 0 && ` (${meta.join(", ")})`}.
        </span>
      );
    }

    case "Customer Payment": {
      if (!amt && !party) return cleanupDetail(log.detail);

      return (
        <span>
          Payment received
          {amt && <>{" "}of <B>{amt} {currency}</B></>}
          {party && <>{" "}from <B>{party}</B></>}
          {status && ` — ${status}`}.
        </span>
      );
    }

    case "Vendor Payment": {
      if (!amt && !party) return cleanupDetail(log.detail);

      return (
        <span>
          Payment sent
          {amt && <>{" "}of <B>{amt} {currency}</B></>}
          {party && <>{" "}to <B>{party}</B></>}.
        </span>
      );
    }

    case "Bill": {
      if (!party && !amt) return cleanupDetail(log.detail);

      const billMeta: string[] = [];
      if (memo) billMeta.push(`Memo: ${memo}`);
      if (status) billMeta.push(`Status: ${status}`);
      if (due) billMeta.push(`Due: ${due}`);

      return (
        <span>
          Bill
          {amt && <>{" "}for <B>{amt} {currency}</B></>}
          {party && <>{" "}from <B>{party}</B></>}
          {billMeta.length > 0 && ` (${billMeta.join(", ")})`}.
        </span>
      );
    }

    case "Expense": {
      if (!amt && !party) return cleanupDetail(log.detail);

      return (
        <span>
          Expense
          {amt && <>{" "}of <B>{amt} {currency}</B></>}
          {party && <>{" "}paid to <B>{party}</B></>}
          {memo && ` (${memo})`}.
        </span>
      );
    }

    case "Customer": {
      if (!party && !name) return cleanupDetail(log.detail);
      const customerName = party || name;
      return (
        <span>
          Customer <B>{customerName}</B> {action || "updated"}.
        </span>
      );
    }

    case "Vendor": {
      if (!party && !name) return cleanupDetail(log.detail);
      const vendorName = party || name;
      return (
        <span>
          Vendor <B>{vendorName}</B> {action || "updated"}.
        </span>
      );
    }

    case "Credit Memo": {
      if (!name && !party && !amt) return cleanupDetail(log.detail);

      return (
        <span>
          Credit Memo {name && <B>{name}</B>}
          {amt && <>{" "}for <B>{amt} {currency}</B></>}
          {party && <>{" "}issued to <B>{party}</B></>}.
        </span>
      );
    }

    case "Estimate": {
      if (!name && !party && !amt) return cleanupDetail(log.detail);

      const estMeta: string[] = [];
      if (status) estMeta.push(`Status: ${status}`);
      if (due) estMeta.push(`Valid until: ${due}`);

      return (
        <span>
          Estimate {name && <B>{name}</B>}
          {amt && <>{" "}for <B>{amt} {currency}</B></>}
          {party && <>{" "}created for <B>{party}</B></>}
          {estMeta.length > 0 && ` (${estMeta.join(", ")})`}.
        </span>
      );
    }

    case "Purchase Order": {
      if (!name && !party && !amt) return cleanupDetail(log.detail);

      return (
        <span>
          Purchase Order {name && <B>{name}</B>}
          {amt && <>{" "}for <B>{amt} {currency}</B></>}
          {party && <>{" "}sent to <B>{party}</B></>}
          {status && ` (Status: ${status})`}.
        </span>
      );
    }

    case "Sync": {
      const source = party || log.counterparty;
      if (isCreated || activityType.includes("started"))
        return source ? <span>Sync with <B>{source}</B> started.</span> : "Sync started.";
      if (activityType.includes("completed") || activityType.includes("finished"))
        return source ? <span>Sync with <B>{source}</B> completed.</span> : "Sync completed.";
      if (activityType.includes("failed"))
        return source ? <span>Sync with <B>{source}</B> failed.</span> : "Sync failed.";
      return cleanupDetail(log.detail);
    }

    case "Deposit": {
      if (!amt && !party) return cleanupDetail(log.detail);

      return (
        <span>
          Deposit
          {amt && <>{" "}of <B>{amt} {currency}</B></>}
          {party && <>{" "}to <B>{party}</B></>}.
        </span>
      );
    }

    case "Transfer": {
      if (!amt && !party) return cleanupDetail(log.detail);

      return (
        <span>
          Transfer
          {amt && <>{" "}of <B>{amt} {currency}</B></>}
          {party && <>{" "}to <B>{party}</B></>}
          {memo && ` (${memo})`}.
        </span>
      );
    }

    case "Journal Entry": {
      return (
        <span>
          Journal Entry {name && <B>{name}</B>}
          {memo && ` (${memo})`}.
        </span>
      );
    }

    default:
      break;
  }

  return cleanupDetail(log.detail);
};

// ---------------------------------------------------------------------------
// Timestamp formatting
// ---------------------------------------------------------------------------

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid date";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// ---------------------------------------------------------------------------
// Map raw ActivityLog → FeedItem
// ---------------------------------------------------------------------------

const toFeedItem = (log: ActivityLog, index: number): FeedItem => {
  const category = resolveCategory(log);
  return {
    id: `${log.timestamp}-${log.activity_type}-${index}`,
    category,
    narrative: buildNarrative(log, category),
    relativeTime: formatTimestamp(log.created_at || log.timestamp),
    direction: resolveDirection(category),
    raw: log,
  };
};

// ---------------------------------------------------------------------------
// Icon & color helpers
// ---------------------------------------------------------------------------

const CATEGORY_ICON: Record<FeedCategory, React.ComponentType<{ className?: string }>> = {
  Invoice: Receipt,
  "Customer Payment": DollarSign,
  "Vendor Payment": CreditCard,
  Bill: FileCheck,
  Expense: CreditCard,
  "Credit Memo": FileMinus,
  Estimate: FileText,
  "Purchase Order": Truck,
  "Journal Entry": FileText,
  Transfer: Landmark,
  Deposit: Landmark,
  Customer: Users,
  Vendor: Users,
  Sync: RefreshCw,
  Other: FileText,
};

const directionStyles: Record<
  MoneyDirection,
  { bg: string; iconColor: string; label: string }
> = {
  in: {
    bg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    label: "text-emerald-700",
  },
  out: {
    bg: "bg-red-50",
    iconColor: "text-red-500",
    label: "text-red-700",
  },
  neutral: {
    bg: "bg-slate-100",
    iconColor: "text-slate-500",
    label: "text-slate-600",
  },
};

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------

const CATEGORY_OPTIONS: FeedCategory[] = [
  "Invoice",
  "Customer Payment",
  "Vendor Payment",
  "Bill",
  "Expense",
  "Credit Memo",
];

const matchesDirection = (dir: MoneyDirection, filter: DirectionFilter) => {
  if (filter === "all") return true;
  if (filter === "money_in") return dir === "in";
  if (filter === "money_out") return dir === "out";
  if (filter === "documents") return dir === "neutral";
  return true;
};

// ---------------------------------------------------------------------------
// Grouping logic
// ---------------------------------------------------------------------------

interface GroupedFeedItem {
  representative: FeedItem;
  items: FeedItem[];
  isGroup: boolean;
  summary: string;
  totalAmount: number | null;
  groupKey: string;
}

const CATEGORY_PLURAL: Record<FeedCategory, string> = {
  Invoice: "invoices",
  "Customer Payment": "customer payments",
  "Vendor Payment": "vendor payments",
  Bill: "bills",
  Expense: "expenses",
  "Credit Memo": "credit memos",
  Estimate: "estimates",
  "Purchase Order": "purchase orders",
  "Journal Entry": "journal entries",
  Transfer: "transfers",
  Deposit: "deposits",
  Customer: "customers",
  Vendor: "vendors",
  Sync: "syncs",
  Other: "activities",
};

const buildGroupSummary = (items: FeedItem[]): { summary: string; totalAmount: number | null } => {
  const category = items[0].category;
  const count = items.length;
  const plural = CATEGORY_PLURAL[category] || "activities";

  // Sum amounts if available
  const amounts = items
    .map((item) => item.raw.amount)
    .filter((a): a is number => a != null);
  const totalAmount = amounts.length > 0 ? amounts.reduce((sum, a) => sum + a, 0) : null;

  // Get unique counterparties
  const parties = new Set(
    items
      .map((item) => item.raw.counterparty || item.raw.entity_display_name)
      .filter(Boolean)
  );

  let summary = `${count} ${plural}`;

  if (totalAmount !== null) {
    const currency = items[0].raw.currency || "USD";
    summary += ` totaling ${formatCurrency(totalAmount, currency)} ${currency}`;
  }

  if (parties.size > 1) {
    summary += ` from ${parties.size} different ${category === "Invoice" || category === "Customer Payment" || category === "Estimate" || category === "Credit Memo" ? "customers" : category === "Bill" || category === "Vendor Payment" || category === "Expense" || category === "Purchase Order" ? "vendors" : "parties"}`;
  } else if (parties.size === 1) {
    summary += ` from ${[...parties][0]}`;
  }

  return { summary, totalAmount };
};

const groupFeedItems = (items: FeedItem[]): GroupedFeedItem[] => {
  if (items.length === 0) return [];

  const groups: GroupedFeedItem[] = [];
  let currentGroup: FeedItem[] = [items[0]];

  for (let i = 1; i < items.length; i++) {
    if (items[i].category === currentGroup[0].category) {
      currentGroup.push(items[i]);
    } else {
      // Flush current group
      if (currentGroup.length === 1) {
        groups.push({
          representative: currentGroup[0],
          items: currentGroup,
          isGroup: false,
          summary: "",
          totalAmount: null,
          groupKey: currentGroup[0].id,
        });
      } else {
        const { summary, totalAmount } = buildGroupSummary(currentGroup);
        groups.push({
          representative: currentGroup[0],
          items: [...currentGroup],
          isGroup: true,
          summary,
          totalAmount,
          groupKey: `group-${currentGroup[0].category}-${currentGroup[0].id}`,
        });
      }
      currentGroup = [items[i]];
    }
  }

  // Flush last group
  if (currentGroup.length === 1) {
    groups.push({
      representative: currentGroup[0],
      items: currentGroup,
      isGroup: false,
      summary: "",
      totalAmount: null,
      groupKey: currentGroup[0].id,
    });
  } else {
    const { summary, totalAmount } = buildGroupSummary(currentGroup);
    groups.push({
      representative: currentGroup[0],
      items: [...currentGroup],
      isGroup: true,
      summary,
      totalAmount,
      groupKey: `group-${currentGroup[0].category}-${currentGroup[0].id}`,
    });
  }

  return groups;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ActivityTabProps {
  onUnreadCountChange?: (count: number) => void;
}

const ActivityTab = ({ onUnreadCountChange }: ActivityTabProps) => {
  const isMobile = useIsMobile();

  // Data state
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Applied filter state (drives fetches)
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [activityTypeFilter, setActivityTypeFilter] = useState<DirectionFilter>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  // Pending filter state (staged in the filter bar)
  const [pendingCategory, setPendingCategory] = useState<string>("all");
  const [pendingDirection, setPendingDirection] = useState<DirectionFilter>("all");
  const [pendingDateFrom, setPendingDateFrom] = useState<string>("");
  const [pendingDateTo, setPendingDateTo] = useState<string>("");

  const hasFilterChanges =
    pendingCategory !== categoryFilter ||
    pendingDirection !== activityTypeFilter ||
    pendingDateFrom !== dateFrom ||
    pendingDateTo !== dateTo;

  const hasActiveFilters =
    categoryFilter !== "all" || activityTypeFilter !== "all" || dateFrom !== "" || dateTo !== "" ||
    pendingCategory !== "all" || pendingDirection !== "all" || pendingDateFrom !== "" || pendingDateTo !== "";

  // Available activity types from server
  const [activityTypes, setActivityTypes] = useState<string[]>([]);

  // Fetch activity types on mount
  useEffect(() => {
    const fetchActivityTypes = async () => {
      try {
        const types = await apiClient.get<string[]>("/activity-logs/types");
        setActivityTypes(types || []);
      } catch (error) {
        console.error("Error fetching activity types:", error);
      }
    };
    fetchActivityTypes();
  }, []);

  // Unread count - no default unread, all start as read
  useEffect(() => {
    onUnreadCountChange?.(0);
  }, [onUnreadCountChange]);

  const handleApplyFilters = () => {
    setCategoryFilter(pendingCategory);
    setActivityTypeFilter(pendingDirection);
    setDateFrom(pendingDateFrom);
    setDateTo(pendingDateTo);
  };

  const clearFilters = () => {
    setPendingCategory("all");
    setPendingDirection("all");
    setPendingDateFrom("");
    setPendingDateTo("");
    setCategoryFilter("all");
    setActivityTypeFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  // Fetch logs
  const fetchActivityLogs = useCallback(
    async (pageNum: number) => {
      if (loadingRef.current) return;
      try {
        loadingRef.current = true;
        setLoading(true);
        const params = new URLSearchParams();
        params.append("page", pageNum.toString());
        params.append("perPage", ITEMS_PER_PAGE.toString());

        if (categoryFilter !== "all") {
          params.append("category", categoryFilter);
        }
        // Direction filter (money_in/money_out/documents) is applied client-side only
        if (dateFrom) params.append("dateFrom", dateFrom);
        if (dateTo) params.append("dateTo", dateTo);

        const response = await apiClient.getRaw<{
          success: boolean;
          data: ActivityLog[];
          total: number;
          page: number;
          perPage: number;
        }>(`/activity-logs?${params.toString()}`);

        const data = response?.data || [];
        const total = response?.total || 0;

        if (pageNum === 1) {
          setActivityLogs(data);
        } else {
          setActivityLogs((prev) => [...prev, ...data]);
        }
        setHasMore(pageNum * ITEMS_PER_PAGE < total);
      } catch (error) {
        console.error("Error fetching activity logs:", error);
      } finally {
        loadingRef.current = false;
        setLoading(false);
        if (pageNum === 1) setInitialLoading(false);
      }
    },
    [categoryFilter, dateFrom, dateTo]
  );

  // Reset on server-side filter change
  useEffect(() => {
    setPage(1);
    setActivityLogs([]);
    setHasMore(true);
    fetchActivityLogs(1);
  }, [categoryFilter, dateFrom, dateTo, fetchActivityLogs]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prev) => {
            const next = prev + 1;
            fetchActivityLogs(next);
            return next;
          });
        }
      },
      { threshold: 0.1 }
    );
    const target = observerTarget.current;
    if (target) observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [hasMore, loading, fetchActivityLogs]);

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  // Build feed items
  const feedItems: FeedItem[] = activityLogs.map(toFeedItem);

  // Client-side direction filter (applied after server response)
  const visibleItems = feedItems.filter((item) =>
    matchesDirection(item.direction, activityTypeFilter)
  );

  // Group consecutive items of the same category
  const groupedItems = groupFeedItems(visibleItems);

  // ------- Render -------

  if (initialLoading) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold mb-6">Activity Log</h2>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading activity&hellip;</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold">Activity Log</h2>
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-0 px-2.5 py-1.5 bg-muted/20 rounded-lg border">
        {/* Scrollable pills area */}
        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 pr-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />

          {/* Category pill */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[160px] ${pendingCategory !== "all" ? "bg-muted/70" : "bg-background"}`}>
                <span className="text-muted-foreground shrink-0">Category:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {pendingCategory === "all" ? "All" : pendingCategory.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
              <button onClick={() => setPendingCategory("all")}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                <Check className={`h-3.5 w-3.5 shrink-0 ${pendingCategory === "all" ? "opacity-100" : "opacity-0"}`} />
                All Categories
              </button>
              {CATEGORY_OPTIONS.map((cat) => {
                const val = cat.toLowerCase().replace(/\s+/g, "_");
                return (
                  <button key={cat} onClick={() => setPendingCategory(val)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                    <Check className={`h-3.5 w-3.5 shrink-0 ${pendingCategory === val ? "opacity-100" : "opacity-0"}`} />
                    {cat}
                  </button>
                );
              })}
              {activityTypes
                .filter(t => !CATEGORY_OPTIONS.some(c => c.toLowerCase().replace(/\s+/g, "_") === t.toLowerCase().replace(/\s+/g, "_")))
                .map(type => (
                  <button key={type} onClick={() => setPendingCategory(type)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                    <Check className={`h-3.5 w-3.5 shrink-0 ${pendingCategory === type ? "opacity-100" : "opacity-0"}`} />
                    {type.replace(/_/g, " ")}
                  </button>
                ))}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Direction pill */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[140px] ${pendingDirection !== "all" ? "bg-muted/70" : "bg-background"}`}>
                <span className="text-muted-foreground shrink-0">Type:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {pendingDirection === "all" ? "All" : pendingDirection === "money_in" ? "Money In" : pendingDirection === "money_out" ? "Money Out" : "Documents"}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[160px] p-1" align="start">
              {(["all", "money_in", "money_out", "documents"] as const).map(d => (
                <button key={d} onClick={() => setPendingDirection(d)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${pendingDirection === d ? "opacity-100" : "opacity-0"}`} />
                  {d === "all" ? "All" : d === "money_in" ? "Money In" : d === "money_out" ? "Money Out" : "Documents"}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Date range pill */}
          <div className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input shrink-0 transition-colors ${(pendingDateFrom || pendingDateTo) ? "bg-muted/70" : "bg-background"}`}>
            <span className="text-muted-foreground shrink-0">Date:</span>
            <input type="date" value={pendingDateFrom} onChange={e => setPendingDateFrom(e.target.value)}
              className="bg-transparent outline-none text-xs w-28" />
            <span className="text-muted-foreground/50">–</span>
            <input type="date" value={pendingDateTo} onChange={e => setPendingDateTo(e.target.value)}
              className="bg-transparent outline-none text-xs w-28" />
          </div>
        </div>

        {/* Apply / Reset buttons */}
        <div className="flex items-center gap-1.5 shrink-0 pl-2 border-l border-border">
          <Button variant="default" size="sm" onClick={handleApplyFilters}
            className="h-7 text-xs px-2.5 gap-1" disabled={!hasFilterChanges}>
            <Filter className="h-3 w-3" /> Apply
          </Button>
          <Button variant="outline" size="sm" onClick={clearFilters}
            className="h-7 text-xs px-2.5 gap-1" disabled={!hasActiveFilters}>
            <X className="h-3 w-3" /> Reset
          </Button>
        </div>
      </div>

      {/* Activity Feed */}
      <ScrollArea
        className={isMobile ? "h-[calc(100vh-380px)]" : "h-[calc(100vh-290px)]"}
        ref={scrollAreaRef}
      >
        <div className="space-y-3">
          {groupedItems.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">No activity found</p>
            </div>
          ) : (
            <>
              {groupedItems.map((group) => {
                const Icon = CATEGORY_ICON[group.representative.category] || FileText;
                const style = directionStyles[group.representative.direction];

                // Single item — render as before
                if (!group.isGroup) {
                  const item = group.representative;
                  return (
                    <div
                      key={group.groupKey}
                      className="rounded-lg border p-4 transition-all duration-150 bg-card hover:bg-muted/40"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 w-9 h-9 rounded-lg ${style.bg} flex items-center justify-center mt-0.5`}
                        >
                          <Icon className={`w-[18px] h-[18px] ${style.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs font-semibold uppercase tracking-wide ${style.label}`}>
                            {item.category}
                          </span>
                          <p className="text-sm text-foreground mt-1 leading-relaxed font-normal">
                            {item.narrative}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {item.relativeTime}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Grouped items — collapsible card
                const isExpanded = expandedGroups.has(group.groupKey);

                return (
                  <Collapsible
                    key={group.groupKey}
                    open={isExpanded}
                    onOpenChange={() => toggleGroup(group.groupKey)}
                  >
                    <CollapsibleTrigger asChild>
                      <div className="rounded-lg border p-4 transition-all duration-150 bg-card hover:bg-muted/40 cursor-pointer">
                        <div className="flex items-start gap-3">
                          <div
                            className={`flex-shrink-0 w-9 h-9 rounded-lg ${style.bg} flex items-center justify-center mt-0.5`}
                          >
                            <Icon className={`w-[18px] h-[18px] ${style.iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-semibold uppercase tracking-wide ${style.label}`}>
                                {group.representative.category}
                              </span>
                              <span className="text-xs font-medium bg-muted text-muted-foreground rounded-full px-2 py-0.5">
                                {group.items.length}
                              </span>
                            </div>
                            <p className="text-sm text-foreground mt-1 leading-relaxed font-medium">
                              {group.summary}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {group.items[group.items.length - 1].relativeTime} — {group.items[0].relativeTime}
                            </p>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-muted-foreground mt-1 flex-shrink-0 transition-transform duration-200 ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="ml-6 border-l-2 border-muted pl-4 space-y-2 mt-2">
                        {group.items.map((item) => {
                          const ItemIcon = CATEGORY_ICON[item.category] || FileText;
                          const itemStyle = directionStyles[item.direction];

                          return (
                            <div
                              key={item.id}
                              className="rounded-lg border p-3 transition-all duration-150 bg-card hover:bg-muted/40"
                            >
                              <div className="flex items-start gap-3">
                                <div
                                  className={`flex-shrink-0 w-7 h-7 rounded-md ${itemStyle.bg} flex items-center justify-center mt-0.5`}
                                >
                                  <ItemIcon className={`w-[14px] h-[14px] ${itemStyle.iconColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-foreground leading-relaxed font-normal">
                                    {item.narrative}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {item.relativeTime}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}

              {/* Loading */}
              {loading && (
                <div className="flex justify-center py-4">
                  <p className="text-sm text-muted-foreground">
                    Loading more&hellip;
                  </p>
                </div>
              )}

              {/* Scroll sentinel */}
              <div ref={observerTarget} className="h-4" />

              {/* End */}
              {!hasMore && groupedItems.length > 0 && (
                <div className="flex justify-center py-4">
                  <p className="text-xs text-muted-foreground">
                    End of activity
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ActivityTab;
